<template>
  <div
    class="-transcribe-token-editor-menu -keep-token-editor-open"
    :class="{ '-bottom': isBottom }"
    :style="style"
  >
    <div class="-edit-tokens-header">
      <div :class="['-left', { '-error': textTooLongTimeout }]">
        {{ durationIndicator }}
        <span v-if="textTooLongTimeout">
          {{ 'transcribe.tts' | culture }}
        </span>
      </div>
      <div class="-right">
        <div class="-cancel" @click="cancel">
          <i title="Cancel" class="wticon wticon-close_x" />
        </div>
        <div class="-apply" @click="apply">
          <i title="Save" class="wticon wticon-toggle_v" />
        </div>
      </div>
    </div>
    <div
      v-once
      v-text="text"
      contenteditable=""
      ref="textarea"
      class="-edit-tokens-textarea"
      dir="auto"
      :data-originalText="editOptions.originalString"
      @keydown.enter="apply"
      @keydown="onKeyDown"
    />
    <span class="-arrow" :style="{ left: editOptions.rect.left + 'px' }" />
  </div>
</template>

<script>
import moment from 'moment';

export default {
  props: {
    editOptions: { type: Object, required: true },
  },

  data() {
    const timeAvailable = this.editOptions.endTime - this.editOptions.startTime;
    let text = this.editOptions.text;
    if (_.isEmpty(text)) {
      text = this.editOptions.originalString;
    }

    return {
      durationIndicator:
        timeAvailable >= 60
          ? moment.utc(timeAvailable * 1000).format('m:ss.SS')
          : timeAvailable >= 1
          ? `${moment.utc(timeAvailable * 1000).format('s.SS')} sec`
          : 'Less than 1 sec',
      timeAvailable,
      text,
      isBottom: false,
      textTooLong: false,
      maxWords: Math.ceil(timeAvailable / 0.1),
      textTooLongTimeout: null,
    };
  },

  mounted() {
    this.$refs.textarea.addEventListener('input', this.onInput);
    this.$emit('mounted');

    setTimeout(() => {
      this.$refs.textarea.focus();
      this.setTextareaHeight();
    }, 0);
  },

  beforeDestroy() {
    this.$refs.textarea.removeEventListener('input', this.onInput);
    if (this.textTooLongTimeout) {
      clearTimeout(this.textTooLongTimeout);
    }
  },

  computed: {
    style() {
      return {
        top: this.editOptions.rect.top + 'px',
      };
    },

    currentWordsCount() {
      return this.text.trim().split(' ').length;
    },
  },

  methods: {
    async setTextareaHeight() {
      this.$emit('sizeChange');
    },

    setBottom() {
      this.isBottom = true;
    },

    apply() {
      if (this.text === this.editOptions.text) {
        return this.$emit('cancel');
      }
      this.$emit('apply', {
        editOptions: this.editOptions,
        text: this.text.trim(),
      });
    },

    cancel() {
      this.$emit('cancel');
    },

    onInput() {
      this.text = this.$refs.textarea.textContent;
      this.setTextareaHeight();
    },

    onKeyDown(e) {
      switch (e.keyCode) {
        case $.ui.keyCode.ENTER:
          e.preventDefault();
          break;
        case $.ui.keyCode.SPACE:
          if (this.currentWordsCount + 1 >= this.maxWords) {
            this.showTTL();
          }
          break;
        default:
          this.fadeTTL();
          break;
      }
    },

    showTTL() {
      if (this.textTooLongTimeout) {
        clearTimeout(this.textTooLongTimeout);
      }
      this.textTooLongTimeout = setTimeout(() => {
        this.textTooLongTimeout = null;
      }, 5000);
    },

    fadeTTL() {
      if (this.textTooLongTimeout) {
        clearTimeout(this.textTooLongTimeout);
        this.textTooLongTimeout = setTimeout(() => {
          this.textTooLongTimeout = null;
        }, app.TIME.MENTAL.λ);
      }
    },

    moveCursorAtTheEnd() {
      const selection = document.getSelection();
      const range = document.createRange();
      const contenteditable = this.$refs.textarea;

      if (contenteditable.lastChild.nodeType === 3) {
        range.setStart(
          contenteditable.lastChild,
          contenteditable.lastChild.length
        );
      } else {
        range.setStart(contenteditable, contenteditable.childNodes.length);
      }
      selection.removeAllRanges();
      selection.addRange(range);
    },
  },
};
</script>

<style scoped lang="scss">
.-transcribe-token-editor-menu {
  position: absolute;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.2);
  background-color: #ffffff;
  padding: 10px 15px 15px;
  left: 0;
  width: 100%;
  transform: translate(0, calc(-100% - 10px));
  user-select: text;
  z-index: 2;

  &.-bottom {
    transform: translate(0, 30px);
    .-arrow {
      bottom: initial;
      top: -10px;
      transform: rotate(180deg);
    }
  }

  .-edit-tokens-textarea {
    resize: none;
    width: 100%;
    outline: none;
    font-size: 14px;
    padding: 10px 15px;
    border: 1px solid rgba(0, 0, 0, 0.2);

    &:focus {
      border: 1px solid #06a9f4;
    }

    &:empty {
      &:not(:focus) {
        &:before {
          color: rgba(0, 0, 0, 0.25);
          content: attr(data-originalText);
        }
      }
    }
  }

  .-edit-tokens-header {
    display: flex;
    flex-direction: row;
    height: 30px;
    align-items: center;
    .-left {
      &.-error {
        color: $color-normal;
      }
      flex-grow: 1;
      font-size: 12px;
      color: rgba(0, 0, 0, 0.5);
      display: flex;
      flex-direction: row;
      span {
        margin-left: 10px;
      }
    }
    .-right {
      display: flex;
      flex-direction: row;
      > div {
        height: 30px;
        width: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        font-size: 11px;

        &.-cancel {
          color: rgba(0, 0, 0, 0.3);
          &:hover {
            color: rgba(0, 0, 0, 0.75);
          }
        }
        &.-apply {
          background: #06a9f4;
          color: white;
          &:hover {
            background: #0475a9;
          }
        }
      }
    }
  }

  .-arrow {
    width: 20px;
    height: 10px;
    position: absolute;
    bottom: -10px;
    left: 50%;
    overflow: hidden;
    transform: translateX(-50%);

    &:after {
      content: '';
      position: absolute;
      width: 24px;
      height: 12px;
      background: white;
      transform: rotate(45deg);
      top: -14px;
      left: -7px;
      box-shadow: 0 0 5px rgba(0, 0, 0, 0.2);
    }
  }
}
</style>
